import { useEffect } from "react";
import { Modal } from "react-bootstrap";

// Each setup gets its own accent so two near-identical scales read as clearly
// different at a glance. Cycles if there are more setups than colors.
const ACCENTS = ["primary", "success", "info", "warning", "danger"];

/**
 * Pre-capture picker shown ONLY when a site has more than one enabled setup
 * group (a setup group = one weighing scale + one camera). The user picks which
 * group to read weight + image from for this single capture — there is no sticky
 * memory, so the popup appears on every capture while multiple groups exist.
 *
 * Built for a busy (often touch) weighbridge screen: large colour-coded tap
 * targets, each card shows the scale's serial port and the camera host so
 * near-identical setups are distinguishable, and number keys 1–9 pick a setup
 * (Esc cancels) for speed.
 *
 * Props:
 *   show     - whether to display the picker
 *   groups   - array of enabled setup groups ({ name, camera, weighbridge, ... })
 *   onSelect - called with the chosen group
 *   onCancel - called when the user dismisses/cancels (aborts the capture)
 */
export function SetupGroupSelectModal({ show, groups = [], onSelect, onCancel }) {
    // Number keys 1-9 select the matching setup; Esc cancels. Only while open.
    useEffect(() => {
        if (!show) return;
        const onKey = (e) => {
            if (e.key === "Escape") {
                onCancel();
                return;
            }
            const n = parseInt(e.key, 10);
            if (!isNaN(n) && n >= 1 && n <= groups.length) {
                onSelect(groups[n - 1]);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [show, groups, onSelect, onCancel]);

    const cameraLabel = (g) => {
        const host = g && g.camera && (g.camera.host || g.camera.ip);
        return host ? String(host) : "No camera";
    };
    const scaleLabel = (g) => {
        const wb = g && g.weighbridge;
        return [wb && wb.brandName, wb && wb.serialPort].filter(Boolean).join(" · ") || "No scale port set";
    };

    return (
        <Modal show={show} centered backdrop="static" keyboard={false} onHide={onCancel} dialogClassName="sgp-dialog">
            <style>{`
                .sgp-dialog{max-width:440px;}
                .sgp-card{border:1px solid var(--bs-border-color);border-left-width:5px;
                    border-radius:.85rem;padding:.85rem 1rem;display:flex;align-items:center;
                    gap:.9rem;width:100%;text-align:start;cursor:pointer;
                    transition:transform .08s ease,box-shadow .12s ease;}
                .sgp-card:hover{box-shadow:0 .35rem 1.1rem rgba(0,0,0,.12);transform:translateY(-2px);}
                .sgp-card:active{transform:translateY(0);box-shadow:none;}
                .sgp-num{flex:0 0 auto;width:2.5rem;height:2.5rem;border-radius:.65rem;
                    display:flex;align-items:center;justify-content:center;font-weight:700;
                    font-size:1.15rem;color:#fff;}
                .sgp-meta{display:flex;flex-wrap:wrap;gap:.2rem .9rem;margin-top:.2rem;}
            `}</style>
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="bi bi-speedometer2 text-primary me-2" />
                    Choose a weighing setup
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-4 pb-2">
                <p className="text-muted mb-4">
                    This site has more than one scale &amp; camera. Tap the one you're weighing on
                    <span className="d-none d-sm-inline"> — or press its number</span>.
                </p>
                <div className="d-flex flex-column gap-3">
                    {groups.map((g, i) => {
                        const c = ACCENTS[i % ACCENTS.length];
                        return (
                            <button
                                type="button"
                                key={g.primaryId || g.id || i}
                                className={`sgp-card bg-light-${c} border-${c}`}
                                onClick={() => onSelect(g)}
                            >
                                <span className={`sgp-num bg-${c}`}>{i + 1}</span>
                                <span className="flex-grow-1">
                                    <span className="fs-4 fw-bold text-gray-900 d-block">
                                        {g.name || `Setup ${i + 1}`}
                                    </span>
                                    <span className="sgp-meta fs-7">
                                        <span className="text-gray-700">
                                            <i className={`bi bi-hdd-network text-${c} me-1`} />
                                            {scaleLabel(g)}
                                        </span>
                                        <span className="text-gray-700">
                                            <i className={`bi bi-camera-video-fill text-${c} me-1`} />
                                            {cameraLabel(g)}
                                        </span>
                                    </span>
                                </span>
                                <i className={`bi bi-chevron-right fs-2 text-${c}`} />
                            </button>
                        );
                    })}
                </div>
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between">
                <span className="text-muted fs-8 d-none d-sm-inline">
                    Press <kbd>1</kbd>–<kbd>{groups.length}</kbd> · <kbd>Esc</kbd> cancels
                </span>
                <button type="button" className="btn btn-light" onClick={onCancel}>
                    Cancel
                </button>
            </Modal.Footer>
        </Modal>
    );
}
